import { ContentPaste as ContentPasteIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { externalVideoUrl } from './externalVideoUrl';
import { useClipboardPaste } from './useClipboardPaste';

const PASTE_TOOLTIP = 'Paste an image';
const PASTE_MEDIA_TOOLTIP = 'Paste an image or video link';
const PASTE_ARMED_TOOLTIP = 'Press Ctrl+V (⌘V on Mac) now';

const ERROR_CAPTION_SX = {
  position: 'absolute',
  top: 32,
  right: 6,
  color: 'error.main',
  bgcolor: 'rgba(255,255,255,0.92)',
  borderRadius: 1,
  px: 0.5,
} as const;

/**
 * Clipboard-read paste button: an icon that reads the clipboard directly
 * (`navigator.clipboard.read()`) and calls `onFile` with any image found,
 * with tooltip/busy/error feedback and the Firefox "arm, then Ctrl+V"
 * fallback. Shared by AddSlideCard (add a slide) and ImageCarouselSlideControls
 * (replace a slide's image) — both otherwise duplicated this exact control.
 */
export default function SlidePasteButton({
  ariaLabel,
  onFile,
  onVideoUrl,
}: {
  ariaLabel: string;
  onFile: (file: File) => void;
  /** When set, a copied YouTube or Vimeo link (and no image) is routed here instead of ignored. */
  onVideoUrl?: (url: string) => void;
}) {
  const paste = useClipboardPaste((payload) => {
    if (payload.image) {
      onFile(payload.image);
      return;
    }
    const videoUrl = onVideoUrl && externalVideoUrl(payload.text);
    if (videoUrl) onVideoUrl?.(videoUrl);
  });

  return (
    <>
      <Tooltip
        title={paste.armed ? PASTE_ARMED_TOOLTIP : onVideoUrl ? PASTE_MEDIA_TOOLTIP : PASTE_TOOLTIP}
      >
        <span>
          <IconButton
            size="small"
            aria-label={ariaLabel}
            onClick={(event) => {
              event.stopPropagation();
              paste.run();
            }}
            disabled={paste.busy}
            color={paste.armed ? 'primary' : 'default'}
          >
            {paste.busy ? <CircularProgress size={16} /> : <ContentPasteIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Box {...paste.hiddenInputProps} />
      {paste.error && (
        <Typography variant="caption" sx={ERROR_CAPTION_SX}>
          {paste.error}
        </Typography>
      )}
    </>
  );
}
